import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeftIcon,
    DownloadSimpleIcon,
    ArrowClockwiseIcon,
    CoinsIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import ScenesPanel from "@/components/ScenesPanel";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { downloadImage } from "@/lib/downloadImage";

export default function Result() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user, refresh } = useAuth();
    const [downloading, setDownloading] = useState(false);
    const balance = user?.credits ?? 0;

    if (!state?.editedUrl) {
        navigate("/upload", { replace: true });
        return null;
    }

    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        const filename = `haogeili-${state.editId || "edit"}.png`;
        try {
            const result = await downloadImage(state.editedUrl, filename);
            track("download_click", {
                edit_id: state.editId,
                method: result.method,
                ok: result.ok,
            });
            if (!result.ok) {
                toast.error("下载失败，请稍后再试 / Download failed. Please try again.");
                return;
            }
            if (result.method === "new_tab") {
                // iOS Safari / WeChat / QQ webview: `<a download>` is
                // silently ignored. We opened the image in a new tab
                // as a workaround — user has to long-press to save.
                toast(
                    "长按图片保存到相册 / Long-press the image to save to your Photos.",
                    { duration: 6000 },
                );
            } else if (result.method === "web_share") {
                toast.success("已打开分享 / Opened Share Sheet.");
            } else if (result.method !== "web_share_cancelled") {
                toast.success("已保存到下载文件夹 / Saved to Downloads.");
            }
        } catch (e) {
            toast.error("下载失败，请稍后再试 / Download failed. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col">
            <header
                className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/5"
                data-testid="result-header"
            >
                <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="back-home"
                        className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
                    >
                        <ArrowLeftIcon size={16} weight="bold" /> Home
                    </Link>
                    <span className="font-heading font-semibold tracking-tight">
                        Your result
                    </span>
                    <span
                        className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1"
                        data-testid="credit-counter"
                    >
                        <CoinsIcon size={12} weight="fill" />
                        <span className="tabular-nums">{balance}</span>
                        <span className="text-white/40">credits</span>
                    </span>
                </div>
            </header>

            <main className="flex-1 max-w-3xl w-full mx-auto px-6 sm:px-8 py-8 sm:py-12 pb-56 space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    data-testid="before-after-pair"
                >
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <figure className="space-y-2" data-testid="before-figure">
                            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#141417] border border-white/5">
                                <img
                                    src={state.originalUrl}
                                    alt="Before"
                                    className="w-full h-full object-cover"
                                    draggable={false}
                                />
                            </div>
                            <figcaption className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/40 text-center">
                                Before
                            </figcaption>
                        </figure>
                        <figure className="space-y-2" data-testid="after-figure">
                            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#141417] border border-white/15">
                                <img
                                    src={state.editedUrl}
                                    alt="After"
                                    className="w-full h-full object-cover"
                                    draggable={false}
                                />
                            </div>
                            <figcaption className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white text-center">
                                After
                            </figcaption>
                        </figure>
                    </div>
                </motion.div>

                <div
                    className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-center"
                    data-testid="disclaimer"
                >
                    <p className="font-heading text-lg text-white">
                        Same you.{" "}
                        <span className="text-white/50">Upgraded.</span>
                    </p>
                </div>

                <ScenesPanel
                    editedImageUrl={state.editedUrl}
                    editId={state.editId}
                    balance={balance}
                    onBalanceChange={refresh}
                />
            </main>

            <div
                className="fixed bottom-0 inset-x-0 z-50 border-t border-white/5 backdrop-blur-xl bg-[#0A0A0C]/85"
                data-testid="result-action-bar"
            >
                <div className="max-w-3xl mx-auto px-6 sm:px-8 py-4 flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading}
                        data-testid="download-button"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-4 bg-white text-black font-medium hover:bg-white/90 active:scale-[0.99] transition-all disabled:opacity-70"
                    >
                        <DownloadSimpleIcon size={18} weight="bold" />
                        {downloading ? "下载中… / Downloading…" : "下载图片 / Download image"}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/upload")}
                        data-testid="try-another"
                        className="sm:w-auto inline-flex items-center justify-center gap-2 rounded-full py-4 px-6 bg-[#222226] text-white font-medium hover:bg-[#2A2A2F] border border-white/5 transition-all"
                    >
                        <ArrowClockwiseIcon size={16} weight="bold" />
                        Try another image
                    </button>
                </div>
            </div>
        </div>
    );
}
